import { loadClarity } from "./clarity";
import { DEFAULT_IGNORE_ERRORS } from "./ignore-errors";
import { parseRate, scrubHeaders, scrubObject } from "./scrub";

export interface SentryConfig {
  dsn: string | undefined;
  environment?: string | undefined;
  release?: string | undefined;
  /** Defaults to 0 (errors-only). Pass `isProd` for env-aware fallback. */
  tracesSampleRate?: string | undefined;
  /** Default 0 (off) — replays only ship on error. */
  replaysSessionSampleRate?: string | undefined;
  /** Default 1.0 — capture replay when an error is sent. */
  replaysOnErrorSampleRate?: string | undefined;
  isProd?: boolean;
  /**
   * Hostnames whose outbound requests should carry trace headers.
   * Anything not in this list will NOT receive `sentry-trace` / `baggage`,
   * preventing leakage to third-party APIs.
   */
  apiHostnames?: string[];
  /** Extra patterns appended to DEFAULT_IGNORE_ERRORS. */
  ignoreErrors?: (string | RegExp)[];
}

export interface ClarityConfig {
  projectId: string | undefined;
}

export interface ObservabilityConfig {
  sentry?: SentryConfig;
  clarity?: ClarityConfig;
}

export function initObservability(config: ObservabilityConfig): void {
  if (config.sentry?.dsn) {
    // Dynamic import keeps the heavy Sentry SDK out of the main bundle when
    // no DSN is configured. ErrorBoundary stays a static reexport from
    // index.ts and works without Sentry.init.
    void initSentry(config.sentry);
  }
  if (config.clarity?.projectId && typeof window !== "undefined") {
    loadClarity(config.clarity.projectId);
  }
}

async function initSentry(cfg: SentryConfig): Promise<void> {
  const Sentry = await import("@sentry/react");

  const tracesSampleRate = parseRate(cfg.tracesSampleRate, 0);
  const replaysSessionSampleRate = parseRate(cfg.replaysSessionSampleRate, 0);
  const replaysOnErrorSampleRate = parseRate(cfg.replaysOnErrorSampleRate, 1.0);

  const withReplay =
    replaysSessionSampleRate > 0 || replaysOnErrorSampleRate > 0;
  const integrations = [
    Sentry.browserTracingIntegration(),
    ...(withReplay
      ? [
          Sentry.replayIntegration({
            maskAllText: true,
            maskAllInputs: true,
            blockAllMedia: true,
          }),
        ]
      : []),
  ];

  Sentry.init({
    dsn: cfg.dsn,
    environment: cfg.environment,
    release: cfg.release,
    sendDefaultPii: false,
    integrations,
    tracesSampleRate,
    replaysSessionSampleRate,
    replaysOnErrorSampleRate,
    tracePropagationTargets: buildTracePropagationTargets(cfg.apiHostnames),
    ignoreErrors: [...DEFAULT_IGNORE_ERRORS, ...(cfg.ignoreErrors ?? [])],
    beforeSend(event) {
      if (event.request?.headers) {
        event.request.headers = scrubHeaders(event.request.headers);
      }
      if (event.request?.cookies) {
        event.request.cookies = undefined;
      }
      if (event.contexts) {
        event.contexts = scrubObject(event.contexts) as typeof event.contexts;
      }
      return event;
    },
    beforeBreadcrumb(breadcrumb) {
      if (breadcrumb.category === "fetch" || breadcrumb.category === "xhr") {
        if (breadcrumb.data && typeof breadcrumb.data === "object") {
          breadcrumb.data = scrubObject(
            breadcrumb.data,
          ) as typeof breadcrumb.data;
        }
      }
      return breadcrumb;
    },
  });
}

function buildTracePropagationTargets(
  apiHostnames: string[] | undefined,
): (string | RegExp)[] {
  // Same-origin always propagates (Sentry default behaviour).
  const targets: (string | RegExp)[] = ["localhost", /^\//];
  if (apiHostnames) {
    for (const hostname of apiHostnames) {
      // Escape dots, then anchor as hostname match.
      const escaped = hostname.replace(/\./g, "\\.");
      targets.push(new RegExp(`^https?://(?:[^/]*\\.)?${escaped}(?::\\d+)?/`));
    }
  }
  return targets;
}
